import { Star, Quote, ExternalLink, MessageSquarePlus, MapPin } from "lucide-react";
import { TESTIMONIALS, COMPANY } from "@/lib/data";

// Google "G" SVG logo (official brand colors)
function GoogleLogo({ className = "w-6 h-6" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
  );
}

export default function Testimonials() {
  return (
    <section data-testid="testimonials-section" id="testimonials" className="section-pad bg-sand">
      <div className="container-pad mx-auto max-w-7xl">
        {/* Google verified header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="eyebrow justify-center">
            <span className="w-8 h-px bg-teal-500" />
            Reviews & Trust
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-navy tracking-tight mt-5 leading-[1.15]">
            Families across Virginia, DC & Maryland <strong className="font-bold text-teal-600">trust Comfort Care.</strong>
          </h2>

          {/* Google Reviews badge */}
          <div className="mt-8 inline-flex items-center gap-4 bg-white rounded-2xl border border-gray-100 shadow-md px-5 py-3.5">
            <GoogleLogo className="w-8 h-8" />
            <div className="text-left">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
                <span className="ml-1.5 text-navy font-bold">5.0</span>
              </div>
              <div className="text-[11px] text-navy/60 mt-0.5">Google Business Reviews</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
            <a
              href={COMPANY.googleProfileUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-read-reviews"
              className="inline-flex items-center justify-center gap-2 bg-white border border-gray-200 hover:border-teal-400 text-navy rounded-full px-5 py-2.5 text-sm font-semibold transition shadow-sm hover:shadow-md"
            >
              <MapPin className="w-4 h-4 text-teal-500" />
              View on Google
              <ExternalLink className="w-3.5 h-3.5 text-navy/40" />
            </a>
            <a
              href={COMPANY.googleReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-leave-review"
              className="inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white rounded-full px-5 py-2.5 text-sm font-semibold transition shadow-md hover:shadow-lg hover:-translate-y-0.5"
            >
              <MessageSquarePlus className="w-4 h-4" />
              Leave a Google Review
            </a>
          </div>
        </div>

        {/* Testimonial cards */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} data-testid={`testimonial-${i}`} className="bg-white rounded-3xl p-8 md:p-10 border border-gray-100 relative">
              <Quote className="absolute top-6 right-6 w-10 h-10 text-teal-500/15" />
              <div className="flex items-center justify-between mb-5">
                <div className="flex gap-0.5">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-navy/40 font-medium uppercase tracking-widest">
                  <GoogleLogo className="w-3.5 h-3.5" />
                  via Google
                </div>
              </div>
              <p className="text-navy/80 text-lg leading-relaxed font-light">"{t.quote}"</p>
              <div className="mt-6 pt-6 border-t border-gray-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-teal-500/10 flex items-center justify-center text-teal-600 font-semibold">
                  {t.name[0]}
                </div>
                <div>
                  <div className="font-semibold text-navy">{t.name}</div>
                  <div className="text-xs text-navy/55">{t.location}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA panel */}
        <div className="mt-12 bg-white rounded-3xl border border-gray-100 p-8 md:p-10 text-center shadow-[0_10px_40px_-20px_rgba(10,25,47,0.15)]">
          <GoogleLogo className="w-12 h-12 mx-auto" />
          <h3 className="font-heading text-2xl md:text-3xl font-semibold text-navy mt-4">
            Did we get you there safely?
          </h3>
          <p className="text-navy/65 mt-3 max-w-xl mx-auto">
            Your review on Google helps more families across Virginia, DC, and Maryland discover safe, compassionate medical transportation.
          </p>
          <a
            href={COMPANY.googleReviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-cta-bottom"
            className="btn-primary mt-6 inline-flex"
          >
            <MessageSquarePlus className="w-4 h-4" />
            Leave a 5★ Review on Google
          </a>
        </div>
      </div>
    </section>
  );
}
